/**
 * This is the main entrypoint file for the application.
 *
 * When loaded in the client side, the application is rendered in the
 * #root element.
 *
 * When the bundle created from this file is imported in the server
 * side, the exported `renderApp` function can be used for server side
 * rendering.
 *
 * Note that this file is required for the build process.
 */

// Polyfills for older browsers
import 'core-js/features/map';
import 'core-js/features/set';
import 'raf/polyfill';

// Dependency libs
import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { loadableReady } from '@loadable/component';

// Import default styles before other CSS-related modules are imported
// This ensures that the styles in marketplaceDefaults.css are included
// as first ones in the final build CSS build file.
import './styles/marketplaceDefaults.css';

// Configs and store setup
import appSettings from './config/settings';
import defaultConfig from './config/configDefault';
import { LoggingAnalyticsHandler } from './analytics/handlers';
import configureStore from './store';

// Utils
import { createInstance, types as sdkTypes, tokenStore as sdkTokenStore } from './util/sdkLoader';
import { createValidatingTokenStore } from './util/tokenStore';
import { mergeConfig } from './util/configHelpers';
import { matchPathname } from './util/routes';
import * as apiUtils from './util/api';
import * as log from './util/log';

// Route config
import routeConfiguration from './routing/routeConfiguration';
// App it self
import { ClientApp, renderApp } from './app';

// TODO: Clean up unused duck imports after runtime errors are resolved
// Auth duck imports
import {
  authenticationInProgress as _authenticationInProgress,
  authInfoRequest as _authInfoRequest,
  authInfoSuccess as _authInfoSuccess,
  loginRequest as _loginRequest,
  loginSuccess as _loginSuccess,
  loginError as _loginError,
  logoutRequest as _logoutRequest,
  logoutSuccess as _logoutSuccess,
  logoutError as _logoutError,
  signupRequest as _signupRequest,
  signupSuccess as _signupSuccess,
  signupError as _signupError,
  confirmRequest as _confirmRequest,
  confirmSuccess as _confirmSuccess,
  confirmError as _confirmError,
  userLogout as _userLogout,
  authInfo,
  login as _login,
  logout as _logout,
  signup as _signup,
  signupWithIdp as _signupWithIdp,
  isAuthenticated as _isAuthenticated,
  authScopes as _authScopes,
} from './ducks/auth.duck';

// User duck imports
import {
  hasCurrentUserErrors as _hasCurrentUserErrors,
  verificationSendingInProgress as _verificationSendingInProgress,
  currentUserShowRequest as _currentUserShowRequest,
  currentUserShowSuccess as _currentUserShowSuccess,
  currentUserShowError as _currentUserShowError,
  clearCurrentUser as _clearCurrentUser,
  fetchCurrentUserHasListingsSuccess as _fetchCurrentUserHasListingsSuccess,
  fetchCurrentUserProfileListingSuccess as _fetchCurrentUserProfileListingSuccess,
  fetchCurrentUserNotificationsSuccess as _fetchCurrentUserNotificationsSuccess,
  fetchCurrentUserHasOrdersSuccess as _fetchCurrentUserHasOrdersSuccess,
  sendVerificationEmailRequest as _sendVerificationEmailRequest,
  sendVerificationEmailSuccess as _sendVerificationEmailSuccess,
  sendVerificationEmailError as _sendVerificationEmailError,
  fetchCurrentUserHasListings as _fetchCurrentUserHasListings,
  fetchCurrentUserHasOrders as _fetchCurrentUserHasOrders,
  fetchCurrentUserNotifications as _fetchCurrentUserNotifications,
  fetchCurrentUserProfileListing as _fetchCurrentUserProfileListing,
  fetchCurrentUser,
  sendVerificationEmail as _sendVerificationEmail,
} from './ducks/user.duck';

// UI duck imports
import { _manageDisableScrolling, _isScrollingDisabled } from './ducks/ui.duck';

// Hosted Assets duck imports
import {
  appAssetsRequested as _appAssetsRequested,
  appAssetsSuccess as _appAssetsSuccess,
  appAssetsError as _appAssetsError,
  pageAssetsRequested as _pageAssetsRequested,
  pageAssetsSuccess as _pageAssetsSuccess,
  pageAssetsError as _pageAssetsError,
  fetchAppAssets,
  fetchPageAssets as _fetchPageAssets,
} from './ducks/hostedAssets.duck';

// Marketplace Data duck imports
import {
  getListingsById as _getListingsById,
  getMarketplaceEntities as _getMarketplaceEntities,
  addMarketplaceEntities as _addMarketplaceEntities,
} from './ducks/marketplaceData.duck';

// Routing duck imports
import { _locationChanged } from './ducks/routing.duck';

// Email Verification duck imports
import {
  verificationInProgress as _verificationInProgress,
  verificationRequest as _verificationRequest,
  verificationSuccess as _verificationSuccess,
  verificationError as _verificationError,
  verify as _verify,
} from './ducks/emailVerification.duck';

// Payment Methods duck - Sonispark uses credits, not real payments

const render = (store, shouldHydrate) => {
  // If the server already loaded the auth information, render the app
  // immediately. Otherwise wait for the flag to be loaded and render
  // when auth information is present.
  const state = store.getState();
  const cdnAssetsVersion = state.hostedAssets.version;
  const authInfoLoaded = state.auth.authInfoLoaded;
  const info = authInfoLoaded ? Promise.resolve({}) : store.dispatch(authInfo());
  info
    .then(() => {
      store.dispatch(fetchCurrentUser());
      // Ensure that Loadable Components is ready
      // and fetch hosted assets in parallel before initializing the ClientApp
      return Promise.all([
        loadableReady(),
        store.dispatch(fetchAppAssets(defaultConfig.appCdnAssets, cdnAssetsVersion)),
      ]);
    })
    .then(([_, fetchedAppAssets]) => {
      const { translations: translationsRaw, ...rest } = fetchedAppAssets || {};
      // We'll handle translations as a separate data.
      // It's given to React Intl instead of pushing to config Context
      const _translations = translationsRaw?.data || {};

      // Rest of the assets are considered as hosted configs
      const configEntries = Object.entries(rest);
      const hostedConfig = configEntries.reduce((collectedData, [name, content]) => {
        return { ...collectedData, [name]: content.data || {} };
      }, {});

      const rootElement = document.getElementById('root');
      if (shouldHydrate) {
        hydrateRoot(
          rootElement,
          <ClientApp store={store} hostedConfig={hostedConfig} /> //hostedTranslations={translations}
        );
      } else {
        const root = createRoot(rootElement);
        root.render(
          <ClientApp store={store} hostedConfig={hostedConfig} /> //hostedTranslations={translations}
        );
      }
    })
    .catch(e => {
      log.error(e, 'browser-side-render-failed');
    });
};

const setupAnalyticsHandlers = () => {
  let handlers = [];

  // Log analytics page views and events in dev mode
  if (appSettings.dev) {
    handlers.push(new LoggingAnalyticsHandler());
  }

  return handlers;
};

// If we're in a browser already, render the client application.
if (typeof window !== 'undefined') {
  // set up logger
  log.setup();

  const baseUrl = appSettings.sdk.baseUrl ? { baseUrl: appSettings.sdk.baseUrl } : {};
  const assetCdnBaseUrl = appSettings.sdk.assetCdnBaseUrl
    ? { assetCdnBaseUrl: appSettings.sdk.assetCdnBaseUrl }
    : {};

  // Create a custom token store that validates token structure
  // This fixes Safari-specific issues where js-cookie may return malformed tokens
  const baseTokenStore = sdkTokenStore.browserCookieStore({
    clientId: appSettings.sdk.clientId,
    secure: appSettings.usingSSL,
  });
  const tokenStore = createValidatingTokenStore(baseTokenStore);

  const preloadedState = window.__PRELOADED_STATE__ || '{}';
  const initialState = JSON.parse(preloadedState, sdkTypes.reviver);
  const sdk = createInstance({
    transitVerbose: appSettings.sdk.transitVerbose,
    clientId: appSettings.sdk.clientId,
    secure: appSettings.usingSSL,
    typeHandlers: apiUtils.typeHandlers,
    tokenStore,
    ...baseUrl,
    ...assetCdnBaseUrl,
  });

  const analyticsHandlers = setupAnalyticsHandlers();
  const store = configureStore(initialState, sdk, analyticsHandlers);

  require('./util/polyfills');
  render(store, !!window.__PRELOADED_STATE__);

  if (appSettings.dev) {
    // Expose stuff for the browser REPL
    window.app = {
      appSettings,
      defaultConfig,
      sdk,
      sdkTypes,
      store,
    };
  }
}

// Show warning if CSP is not enabled
const CSP = process.env.REACT_APP_CSP;
const cspEnabled = CSP === 'block' || CSP === 'report';

if (CSP === 'report' && process.env.REACT_APP_ENV === 'production') {
  console.warn(
    'Your production environment should use CSP with "block" mode. Read more from: https://www.sharetribe.com/docs/ftw-security/how-to-set-up-csp-for-ftw/'
  );
} else if (!cspEnabled) {
  console.warn(
    "CSP is currently not enabled! You should add an environment variable REACT_APP_CSP with the value 'report' or 'block'. Read more from: https://www.sharetribe.com/docs/ftw-security/how-to-set-up-csp-for-ftw/"
  );
}

// Export the function for server side rendering.
export default renderApp;

// exporting matchPathname and configureStore for server side rendering.
// matchPathname helps to figure out which route is called and if it has preloading needs
// configureStore is used for creating initial store state for Redux after preloading
export {
  matchPathname,
  configureStore,
  routeConfiguration,
  defaultConfig,
  mergeConfig,
  fetchAppAssets,
};
